import { motion } from 'framer-motion';
import { Users, UserCheck, MessageSquare, Info } from 'lucide-react';

interface RACIRole {
  id: string;
  name: string;
  description: string;
  icon: any;
  color: string;
}

interface RACIActivity {
  id: string;
  name: string;
  description: string;
  roles: {
    [key: string]: 'R' | 'A' | 'C' | 'I' | '';
  };
}

const raciRoles: RACIRole[] = [
  {
    id: 'directeur-service',
    name: 'Directeur Service TI',
    description: 'Jean-Pierre Leclerc - Responsable de la stratégie et du développement',
    icon: Users,
    color: '#E74C3C'
  },
  {
    id: 'prime',
    name: 'Prime',
    description: 'Directeur TI client - Acteur principal du développement',
    icon: UserCheck,
    color: '#0066FF'
  },
  {
    id: 'gestionnaire',
    name: 'Gestionnaire',
    description: 'Gestionnaire d\'équipe - Support et mentorat',
    icon: Users,
    color: '#00B894'
  },
  {
    id: 'ce',
    name: 'CE (Conseiller Expert)',
    description: 'Conseiller Expert - Expertise technique et conseil',
    icon: Info,
    color: '#F39C12'
  },
  {
    id: 'client',
    name: 'Client',
    description: 'Directeur de compte client - Partie prenante',
    icon: MessageSquare,
    color: '#9B59B6'
  }
];

const raciActivities: RACIActivity[] = [
  {
    id: 'presentation-categories',
    name: 'Présentation des 8 Catégories',
    description: 'Expliquer et détailler les 8 catégories de compétences Prime',
    roles: {
      'directeur-service': 'A',
      'prime': 'R',
      'gestionnaire': 'C',
      'ce': 'C',
      'client': 'I'
    }
  },
  {
    id: 'evaluation-niveau',
    name: 'Évaluation du Niveau Actuel',
    description: 'Déterminer le niveau ASIS de chaque Prime (Junior/Prime/Senior)',
    roles: {
      'directeur-service': 'A',
      'prime': 'R',
      'gestionnaire': 'C',
      'ce': 'C',
      'client': 'I'
    }
  },
  {
    id: 'acquisition-competences',
    name: 'Acquisition des Compétences',
    description: 'Développer et améliorer les compétences selon le référentiel',
    roles: {
      'directeur-service': 'A',
      'prime': 'R',
      'gestionnaire': 'C',
      'ce': 'C',
      'client': 'I'
    }
  },
  {
    id: 'formation-continue',
    name: 'Formation Continue',
    description: 'Suivre les formations et programmes de développement',
    roles: {
      'directeur-service': 'A',
      'prime': 'R',
      'gestionnaire': 'C',
      'ce': 'C',
      'client': 'I'
    }
  },
  {
    id: 'mentorat-prime',
    name: 'Mentorat entre Primes',
    description: 'Consultation et partage d\'expérience entre Primes',
    roles: {
      'directeur-service': 'A',
      'prime': 'R',
      'gestionnaire': 'C',
      'ce': 'C',
      'client': 'I'
    }
  },
  {
    id: 'consultation-gestionnaire',
    name: 'Consultation Gestionnaire',
    description: 'Demander conseil et support aux Gestionnaires',
    roles: {
      'directeur-service': 'A',
      'prime': 'R',
      'gestionnaire': 'C',
      'ce': 'C',
      'client': 'I'
    }
  },
  {
    id: 'consultation-ce',
    name: 'Consultation CE',
    description: 'Solliciter l\'expertise des Conseillers Experts',
    roles: {
      'directeur-service': 'A',
      'prime': 'R',
      'gestionnaire': 'C',
      'ce': 'C',
      'client': 'I'
    }
  },
  {
    id: 'suivi-progression',
    name: 'Suivi de Progression',
    description: 'Mesurer l\'évolution ASIS vers Target',
    roles: {
      'directeur-service': 'A',
      'prime': 'R',
      'gestionnaire': 'C',
      'ce': 'C',
      'client': 'I'
    }
  },
  {
    id: 'communication-client',
    name: 'Communication Client',
    description: 'Informer le client des développements et améliorations',
    roles: {
      'directeur-service': 'A',
      'prime': 'R',
      'gestionnaire': 'C',
      'ce': 'C',
      'client': 'I'
    }
  },
  {
    id: 'rapport-excellence',
    name: 'Rapport d\'Excellence',
    description: 'Présenter le profil Spider et les KPIs au client',
    roles: {
      'directeur-service': 'A',
      'prime': 'R',
      'gestionnaire': 'C',
      'ce': 'C',
      'client': 'I'
    }
  }
];

export default function PrimeRACITable() {
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'R': return 'bg-red-100 text-red-800 border-red-300';
      case 'A': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'C': return 'bg-green-100 text-green-800 border-green-300';
      case 'I': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-white text-gray-400 border-gray-200';
    }
  };


  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-bz-dark to-bz-blue text-white p-6">
        <h3 className="text-2xl font-bold mb-2">📋 Matrice RACI - Développement Prime</h3>
        <p className="text-white/90">
          Répartition des rôles et responsabilités pour l'excellence des Primes BZ
        </p>
      </div>

      <div className="p-6">
        {/* Légende RACI */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-3">Légende RACI</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="flex items-center gap-2 p-2 bg-red-50 rounded border border-red-200">
              <div className="w-6 h-6 bg-red-500 text-white text-xs font-bold rounded flex items-center justify-center">R</div>
              <div>
                <div className="text-sm font-medium text-red-800">Responsable</div>
                <div className="text-xs text-red-600">Exécute l'activité</div>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 bg-blue-50 rounded border border-blue-200">
              <div className="w-6 h-6 bg-blue-500 text-white text-xs font-bold rounded flex items-center justify-center">A</div>
              <div>
                <div className="text-sm font-medium text-blue-800">Imputable</div>
                <div className="text-xs text-blue-600">Responsable du résultat</div>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
              <div className="w-6 h-6 bg-green-500 text-white text-xs font-bold rounded flex items-center justify-center">C</div>
              <div>
                <div className="text-sm font-medium text-green-800">Consulté</div>
                <div className="text-xs text-green-600">Peut être sollicité</div>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200">
              <div className="w-6 h-6 bg-gray-500 text-white text-xs font-bold rounded flex items-center justify-center">I</div>
              <div>
                <div className="text-sm font-medium text-gray-800">Informé</div>
                <div className="text-xs text-gray-600">Tenus au courant</div>
              </div>
            </div>
          </div>
        </div>

        {/* Rôles */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-3">Acteurs</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {raciRoles.map((role, index) => {
              const Icon = role.icon;
              return (
                <motion.div
                  key={role.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border"
                >
                  <div 
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${role.color}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: role.color }} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-800">{role.name}</div>
                    <div className="text-xs text-gray-600">{role.description}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Tableau RACI */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-3 border-b font-semibold text-gray-800 min-w-[200px]">
                  Activités
                </th>
                {raciRoles.map((role) => (
                  <th key={role.id} className="text-center p-3 border-b font-semibold text-gray-800 min-w-[100px]">
                    <div className="text-sm">{role.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {raciActivities.map((activity, index) => (
                <motion.tr
                  key={activity.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b hover:bg-gray-50"
                >
                  <td className="p-3 border-r">
                    <div>
                      <div className="font-medium text-gray-800">{activity.name}</div>
                      <div className="text-xs text-gray-600 mt-1">{activity.description}</div>
                    </div>
                  </td>
                  {raciRoles.map((role) => {
                    const roleValue = activity.roles[role.id];
                    return (
                      <td key={role.id} className="text-center p-3">
                        {roleValue && (
                          <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full border-2 font-bold text-sm ${getRoleColor(roleValue)}`}>
                            {roleValue}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Notes importantes */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2">📌 Points Clés</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Jean-Pierre Leclerc</strong> a l'imputabilité (A) de présenter et expliquer les 8 catégories</li>
            <li>• Les <strong>Primes</strong> sont responsables (R) d'acquérir et améliorer leurs compétences</li>
            <li>• Les Primes peuvent <strong>consulter</strong> n'importe quel collègue Prime, Gestionnaire ou CE</li>
            <li>• Les <strong>clients</strong> sont informés (I) des développements et peuvent être consultés</li>
            <li>• Le suivi ASIS → Target est <strong>collaboratif</strong> avec support de l'équipe</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
